import { Component, OnInit, inject, viewChild } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    Data,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterOutlet,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { LoadingIndicatorComponent } from '@components/shared';
import { setUser } from '@sentry/browser';
import { AnalyticsService } from '@services/analytics.service';
import { AppUpdateService } from '@services/app-update.service';
import { CountryService } from '@services/country.service';
import { EnvironmentService } from '@services/environment.service';
import { PlausibleService } from '@services/plausible.service';
import { UserRoleService } from '@services/user-role.service';
import { UserSettingsService } from '@services/user-settings.service';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SplashScreenComponent } from './components/splash-screen/splash-screen.component';
import { ICONS } from './constants';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    imports: [LoadingIndicatorComponent, SplashScreenComponent, RouterOutlet],
})
export class AppComponent implements OnInit {
    private authService = inject(AuthService);
    private userSettingsService = inject(UserSettingsService);
    private gaService = inject(AnalyticsService);
    private userRoleService = inject(UserRoleService);
    private countryService = inject(CountryService);
    private titleService = inject(Title);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private environmentService = inject(EnvironmentService);
    private window = inject(Window);
    private appUpdateService = inject(AppUpdateService);
    private plausibleService = inject(PlausibleService);
    matIconRegistry = inject(MatIconRegistry);
    sanitizer = inject(DomSanitizer);

    private readonly loadingIndicator = viewChild(LoadingIndicatorComponent);

    constructor() {
        for (const icon of ICONS) {
            this.matIconRegistry.addSvgIcon(
                icon,
                this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${icon}.svg`), // NOSONAR
            );
        }
        this.appUpdateService.handleUpdates();
        this.plausibleService.initialize();
    }

    ngOnInit(): void {
        // we show loading spinner during the bootstrap phase of angular
        this.showLoadingSpinner();

        this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
            if (isAuthenticated) {
                combineLatest([
                    this.userSettingsService.getHashedUserId(),
                    this.userSettingsService.getUserName(),
                    this.userRoleService.getUserRole(),
                ]).subscribe((result) => {
                    const hashedUserId = result[0];
                    const userName = result[1];
                    const userRoleResponse = result[2];
                    const role = userRoleResponse.data.responsibilityId;
                    localStorage.setItem('responsibilityId', role);

                    setUser({
                        id: hashedUserId,
                        username: userName,
                        role: role,
                    });

                    this.gaService.set(
                        hashedUserId,
                        role,
                        this.countryService.getCurrentCultureInfo(),
                        this.countryService.getCurrentCountry(),
                    );
                });

                return;
            }

            const authorizationParams = {
                authorizationParams: {
                    connection: this.environmentService.getEnvinronment().auth.connection,
                },
            };

            this.authService.loginWithRedirect(authorizationParams);
        });

        this.setTitle();
    }

    private showLoadingSpinner(): void {
        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationStart ||
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError,
                ),
            )
            .subscribe((event) => {
                if (event instanceof NavigationStart) {
                    this.loadingIndicator().show();
                    return;
                }
                this.loadingIndicator().hide();
            });
    }

    private setTitle(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
            )
            .subscribe((event: ActivatedRoute) => {
                const data: Data = event.snapshot.data;
                const currentPagetitle: string = `Lettings Application-${data['pageTitle']}`;
                this.titleService.setTitle(currentPagetitle);
                const url = this.window.location.href;
                this.gaService.pageView(currentPagetitle, url);
            });
    }
}
